import { LineItemType, type RelatedStackItem } from "legacyComponents/LineItemContainer.types";

import type { InvoiceLineItemMetadata } from "@btnet/services-btnet/handlers/models/invoiceLineItemMetadata";
import type { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";

import { type CostTypes, type MarkedAs } from "types/enum";

import yup from "utilities/form/yup";
import { calculateBuilderCost, calculateMarginPercentage } from "utilities/lineItem/lineItemHelper";
import { getMarginPercentageFromMarkupPercentage } from "utilities/lineItem/markupUtility";
import { isNullOrUndefined } from "utilities/object/object";

import { type IInvoiceRelatedItemProps } from "commonComponents/utilities/LineItemContainer/cellImplementations/InvoiceRelatedItemCell";
import { type ILineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    baseLineItemSchemaDefinition,
    markupLineItemSchemaDefinition,
    taxLineItemSchemaDefinition,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.schemas";
import {
    type IBaseLineItem,
    type IMarkupLineItem,
    type ITaxLineItem,
    type MarkupSource,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { type ILineItemRelatedItem } from "entity/hawk/api/lineItem.api.types";
import {
    type ILineItemResponseWithMetaData,
    InvoiceLineItemWithMetadata,
} from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.api.types";

export interface IOwnerInvoiceLineItem extends IMarkupLineItem, ITaxLineItem, ILineItemRow {
    // stored in database with markup fields, but they can all be calculated from ownerPrice
    // unitPrice comes from markupPerUnit in database
    unitCost: number;
    quantity: number;
    ownerPrice: number;
    internalNotes: string;
    costItemId: number | null;
    costCodeTitle: string | null | undefined;
    lineItemType: LineItemType;
    costTypes: CostTypes[];
    markedAs: MarkedAs;
    unitPrice: number;
    relatedChangeOrderLineItemId?: number | null;
    relatedSelectionChoiceLineItemId?: number | null;
    relatedGeneralItemId?: number | null;
    relatedBidLineItemId?: number | null;
    relatedBillLineItemId?: number | null;
    relatedTimeCardItemId?: number | null;
    relatedTimeCardLineItemId?: number | null;
    relatedAccountingCostId?: number | null;
    relatedStackLinks?: Record<number, RelatedStackItem[]> | null;
    /**
     * Selections Invoicing Wizard allowance group id (ado-281495). Shared by the wizard's credit line and
     * its selection lines so a whole allowance reconciliation deletes as one group. Not persisted on the
     * invoice line item — set from the wizard response on in-session add, and re-derived from FKs at load.
     */
    selectionOptionGroupID?: number | null;
    /**
     * Standalone-selection group key (SelectionOptionID). Shared by the wizard's per-choice selection lines
     * of a single non-allowance selection so they delete as one group. Not persisted on the invoice line
     * item — set from the wizard response on in-session add, and re-derived from the choice-line-item FK at load.
     */
    selectionOptionID?: number | null;
    isStackedLineItem: boolean;
    allowanceLineItemId?: number | null;
    allowanceId?: number | null;
    calculatedAmount?: number;
    relatedItem?: IInvoiceRelatedItemProps;
    relatedItems?: ILineItemRelatedItem[];
    /**
     * Field to indicate what has been invoiced from a related entity, but not saved yet
     */
    newlyInvoicedAmount?: number;
    invoiceLineItemMetadata?: InvoiceLineItemMetadata;
    retainageWithheld?: number;
    storedMaterials?: number;
}

/**
 * Whether a working-set invoice line item should feed the "Add from..." flows' already-on-invoice
 * dedup (`newlyInvoicedLineItems`).
 *
 * `newlyInvoicedAmount` only marks unsaved related-entity adds, so it disappears once the line is
 * persisted. The selections wizard's only dedup is id-based and has no invoiceId-aware backend
 * filter, so a saved selection added at $0 this period (progress invoices) would otherwise be
 * re-offered and double-added. Including any line that carries a `relatedSelectionChoiceLineItemId`
 * keeps saved selections deduped; other entity types still rely on their invoiceId-aware endpoint
 * for saved dedup, so they only need the unsaved `newlyInvoicedAmount` signal here.
 */
export const isDedupableInvoiceLineItem = (lineItem: IOwnerInvoiceLineItem): boolean =>
    lineItem.newlyInvoicedAmount != null || lineItem.relatedSelectionChoiceLineItemId != null;

export class OwnerInvoiceLineItem implements IOwnerInvoiceLineItem {
    constructor(data: {
        selectionOptionGroupID?: number | null;
        selectionOptionID?: number | null;
        [key: string]: any;
    }) {
        this._isInEdit = data._isInEdit;
        this.autoFocusedField = data.autoFocusedField;
        if (data.builderCost) {
            this.builderCost = data.builderCost;
        } else {
            this.builderCost = calculateBuilderCost(data.unitCost, data.quantity);
        }
        this.costCodeId = data.costCode ?? data.costCodeId;
        this.costItemId = data.costCodeItemId ?? data.costItemId;
        this.costTypes = data.costTypes ?? [];
        this.markedAs = data.markedAs;
        this.description = data.description;
        this.id = data.lineItemId ?? data.id;
        this.internalNotes = data.internalNotes;
        this.isEditable = true;
        this.isExpanded = data.isExpanded ?? false;
        this.itemTitle = data.itemTitle ?? data.title;
        this.markupAmount = data.markupAmount ?? data.markupPrice;
        this.markupPerUnit = data.markupPerUnit;
        this.markupPercent = data.markupPercent ?? data.markupPercentage;
        // Amounts first, markupPercent as fallback: progress invoice lines arrive with every amount
        // zeroed, while bill/accounting/time-card lines arrive with markupPercent 0. Either source
        // alone yields a 0 that reads as "bills at cost" and erases the markup. With neither, the
        // margin is unknown, not 0.
        this.margin =
            data.margin ??
            (data.ownerPrice
                ? calculateMarginPercentage(this.builderCost, data.ownerPrice)
                : isNullOrUndefined(this.markupPercent)
                  ? undefined
                  : getMarginPercentageFromMarkupPercentage(this.markupPercent));
        this._markupSource = data.markupSource;
        this.ownerPrice = data.ownerPrice;
        this.quantity = data.quantity ?? 0;
        this.unit = data.unitType ?? data.unit;
        this.unitCost = data.unitCost;
        this.unitPrice = data.unitPrice;
        this.calculatedAmount = data.calculatedAmount;
        this.markupType = data.markupType ?? data.markupColumn;

        this.costCodeTitle = data.costCodeTitle;

        this.lineItemType = LineItemType.OwnerInvoice;
        this.relatedChangeOrderLineItemId = data.relatedChangeOrderLineItemId;
        this.relatedSelectionChoiceLineItemId = data.selectionChoiceLineItemId;
        this.relatedGeneralItemId = data.relatedGeneralItemId;
        this.relatedBidLineItemId = data.relatedBidLineItemId;
        this.relatedBillLineItemId =
            (data as IOwnerInvoiceLineItem).relatedBillLineItemId ?? data.billLineItemId;
        this.relatedAccountingCostId = data.relatedAccountingCostId;
        this.selectionOptionGroupID = data.selectionOptionGroupID;
        this.selectionOptionID = data.selectionOptionID;
        this.taxGroupId = data.taxGroupId;
        this.totalWithTax = data.totalWithTax;
        this.allowanceLineItemId = data.allowanceLineItemId;
        this.allowanceId = data.allowanceId;
        this.relatedStackLinks = data.relatedStackLinks;
        this.relatedItem = data.relatedParentId
            ? {
                  itemLabel: data.relatedParentTitle ?? data.relatedParentId,
                  itemId: data.relatedParentId,
                  itemType: data.relatedParentType,
              }
            : (data as IOwnerInvoiceLineItem).relatedItem;
        this.isStackedLineItem =
            !isNullOrUndefined(this.relatedStackLinks) &&
            (Object.keys(this.relatedStackLinks).length > 1 || // two or more related time cards
                Object.values(this.relatedStackLinks).reduce((acc, arr) => acc + arr.length, 0) >
                    1); // two or more related time line items

        this.relatedItems = data.relatedItems ?? undefined;
        this.newlyInvoicedAmount = (data as IOwnerInvoiceLineItem).newlyInvoicedAmount;
        this.invoiceLineItemMetadata = data.invoiceLineItemMetadata;
        this.retainageWithheld = (data as IOwnerInvoiceLineItem).retainageWithheld;
        this.storedMaterials = (data as IOwnerInvoiceLineItem).storedMaterials;
    }

    _isInEdit?: boolean | undefined;
    autoFocusedField?: string | undefined;
    builderCost: number;
    costCodeId: number | null;
    costCodeTitle: string | null | undefined;
    costItemId: number | null;
    costTypes: CostTypes[];
    markedAs: MarkedAs;
    description: string;
    id: number;
    internalNotes: string;
    isDiscontinued?: boolean | undefined;
    isEditable: boolean;
    isExpanded?: boolean | undefined;
    isSelected?: boolean | undefined;
    itemTitle: string | null;
    lineItemType: LineItemType;
    markupAmount: number;
    markupPerUnit: number;
    markupPercent: number;
    margin?: number; // Owner Invoice line items don't currently have margin, but we can pull in line items from change orders/estimate/etc that use margin
    markupType: MarkupColumnTypes;
    _markupSource?: MarkupSource;
    ownerPrice: number;
    parentId?: number | undefined;
    quantity: number;
    relatedGeneralItemId?: number | null | undefined;
    unit: string;
    unitCost: number;
    unitPrice: number;
    varianceCodeId?: number | null | undefined;
    varianceCodeTitle?: string | undefined;
    relatedChangeOrderLineItemId?: number | null;
    relatedSelectionChoiceLineItemId?: number | null;
    relatedBidLineItemId?: number | null;
    relatedBillLineItemId?: number | null | undefined;
    relatedTimeCardItemId?: number | null;
    relatedTimeCardLineItemId?: number | null;
    relatedAccountingCostId?: number | null;
    selectionOptionGroupID?: number | null;
    selectionOptionID?: number | null;
    relatedStackLinks?: Record<number, RelatedStackItem[]>;
    relatedItems?: ILineItemRelatedItem[];
    relatedItem?: IInvoiceRelatedItemProps;
    isStackedLineItem: boolean;
    /**
     * Field to indicate what has been invoiced from a related entity, but not saved yet
     */
    newlyInvoicedAmount?: number;
    // ITaxLineItem
    taxGroupId?: number;
    totalWithTax?: number;
    allowanceLineItemId?: number;
    allowanceId?: number;
    calculatedAmount?: number;
    invoiceLineItemMetadata?: InvoiceLineItemMetadata;
    retainageWithheld?: number;
    storedMaterials?: number;
}

const baseLineItemOwnerInvoiceSchemaType = {
    ...baseLineItemSchemaDefinition,
    ...markupLineItemSchemaDefinition,
    ...taxLineItemSchemaDefinition,
};
type BaseLineItemOwnerInvoiceSchemaType = typeof baseLineItemOwnerInvoiceSchemaType;
export function getLineItemOwnerInvoiceSchema(
    getExtendedSchema: (
        baseSchema: BaseLineItemOwnerInvoiceSchemaType,
    ) => BaseLineItemOwnerInvoiceSchemaType = (s) => s,
) {
    return yup.object<IBaseLineItem>(getExtendedSchema(baseLineItemOwnerInvoiceSchemaType));
}

export const transformOwnerInvoiceLineItemsToLineItems = (
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[],
): InvoiceLineItemWithMetadata[] => {
    return ownerInvoiceLineItems.map((x: IOwnerInvoiceLineItem) => {
        return new InvoiceLineItemWithMetadata(
            {
                id: x.id,
                costCodeId: x.costCodeId,
                costCode: x.costCodeId!,
                costCodeItemId: x.costItemId,
                catalogItemId: x.costItemId,
                costTypeId: 0,
                unitCost: x.unitCost,
                quantity: x.quantity,
                unitType: x.unit,
                builderCost: x.builderCost,
                markupColumn: x.markupType as number,
                markupPercentage: x.markupPercent,
                margin: x.margin,
                markupPerUnit: x.markupPerUnit,
                markupPrice: x.markupAmount,
                ownerPrice: x.ownerPrice,
                title: x.itemTitle,
                description: x.description,
                internalNotes: x.internalNotes,
                costTypes: x.costTypes,
                markedAs: x.markedAs,
                relatedGeneralItemId: x.relatedGeneralItemId,
                relatedChangeOrderLineItemId: x.relatedChangeOrderLineItemId,
                relatedBidLineItemId: x.relatedBidLineItemId,
                relatedStackLinks: x.relatedStackLinks,
                selectionChoiceLineItemId: x.relatedSelectionChoiceLineItemId,
                selectionOptionGroupID: x.selectionOptionGroupID,
                selectionOptionID: x.selectionOptionID,
                billLineItemId: x.relatedBillLineItemId,
                relatedAccountingCostId: x.relatedAccountingCostId,
                taxGroupId: x.taxGroupId,
                totalWithTax: x.totalWithTax,
                allowanceLineItemId: x.allowanceLineItemId,
                allowanceId: x.allowanceId,
                calculatedAmount: x.calculatedAmount,
                invoiceLineItemMetadata: x.invoiceLineItemMetadata,
                retainageWithheld: x.retainageWithheld,
                storedMaterials: x.storedMaterials,
            } as ILineItemResponseWithMetaData,
            "",
            LineItemType.OwnerInvoice,
        );
    });
};

export enum OwnerInvoiceLineItemContainerColumns {
    TitleAndCostCode = 1,
    CostTypes = 2,
    UnitCost = 3,
    UnitPrice = 4,
    Quantity = 5,
    Unit = 6,
    Markup = 7,
    MarkupAmount = 8,
    OwnerPrice = 9,
    Tax = 10,
    RelatedItems = 11,
    DeleteAction = 12,
    MarkedAs = 13,
}
